import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Link2,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  configureClickupConnection,
  disconnectClickupConnection,
  discoverClickupLifecycle,
  getClickupConnection,
  saveClickupLifecycle,
  startClickupOauth,
  type ClickupConnectionRow,
  type ClickupLifecycleState,
  type ClickupListMappingRow
} from '../api';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatRelativeDate } from '../utils/formatters';

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};

const LIFECYCLE_OPTIONS: Array<{
  value: ClickupLifecycleState;
  label: string;
}> = [
  { value: 'planned', label: 'Planned' },
  { value: 'ready', label: 'Ready' },
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'validation', label: 'Validation' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' }
];

function messageFrom(error: unknown, fallback: string) {
  const failure = error as RequestError;
  return failure.response?.data?.message || failure.message || fallback;
}

export function ClickupConnectionPanel() {
  const { session } = useSession();
  const { activeOrganization } = useWorkspace();
  const { confirm, toast } = useFeedback();
  const [connection, setConnection] = useState<ClickupConnectionRow | null>(
    null
  );
  const [workspaceId, setWorkspaceId] = useState('');
  const [spaceIds, setSpaceIds] = useState<string[]>([]);
  const [mappings, setMappings] = useState<ClickupListMappingRow[]>([]);
  const [editingLifecycle, setEditingLifecycle] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!session?.accessToken) return;
    setConnection(null);
    try {
      const result = await getClickupConnection(
        session.accessToken,
        activeOrganization?.id
      );
      setConnection(result);
      setWorkspaceId(
        result.workspaceId || result.availableWorkspaces[0]?.id || ''
      );
      setSpaceIds(result.spaceIds || []);
      setMappings(result.lifecycle?.mappings || []);
    } catch {
      setConnection({
        platformConfigured: false,
        status: 'not_connected',
        availableWorkspaces: [],
        spaceIds: [],
        availableSpaces: [],
        lifecycle: {
          discoveredLists: [],
          mappings: [],
          writeBackEnabled: false,
          writeBackRules: {}
        },
        errorMessage: 'The connector API is not available yet.'
      });
    }
  }, [activeOrganization?.id, session?.accessToken]);
  useEffect(() => {
    void load();
  }, [load]);

  const authorize = async () => {
    if (!session?.accessToken) return;
    setBusy(true);
    try {
      const result = await startClickupOauth(
        session.accessToken,
        window.location.pathname,
        activeOrganization?.id
      );
      window.location.assign(result.authorizeUrl);
    } catch (error: unknown) {
      toast(
        'error',
        messageFrom(error, 'Could not start ClickUp authorization')
      );
      setBusy(false);
    }
  };

  const activate = async () => {
    if (!session?.accessToken || !workspaceId) return;
    setBusy(true);
    try {
      const result = await configureClickupConnection(
        session.accessToken,
        workspaceId,
        spaceIds,
        activeOrganization?.id
      );
      setConnection(result);
      toast('success', 'ClickUp is connected in read-only mode');
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Could not configure ClickUp'));
    } finally {
      setBusy(false);
    }
  };

  const discover = async () => {
    if (!session?.accessToken || !workspaceId) return;
    setBusy(true);
    try {
      const result = await configureClickupConnection(
        session.accessToken,
        workspaceId,
        [],
        activeOrganization?.id
      );
      setConnection(result);
      setSpaceIds([]);
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Could not load ClickUp Spaces'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (
      !session?.accessToken ||
      !(await confirm({
        title: 'Disconnect ClickUp?',
        message:
          'Task updates will stop immediately. Existing delivery evidence stays in Cindro.',
        confirmLabel: 'Disconnect',
        danger: true
      }))
    )
      return;
    setBusy(true);
    try {
      await disconnectClickupConnection(
        session.accessToken,
        activeOrganization?.id
      );
      await load();
      toast('success', 'ClickUp disconnected');
    } catch {
      toast('error', 'Could not disconnect ClickUp');
    } finally {
      setBusy(false);
    }
  };

  const discoverLifecycle = async () => {
    if (!session?.accessToken) return;
    setBusy(true);
    try {
      const result = await discoverClickupLifecycle(
        session.accessToken,
        activeOrganization?.id
      );
      setConnection(result);
      setMappings(result.lifecycle.mappings);
      setEditingLifecycle(true);
      toast(
        'success',
        `Found ${result.lifecycle.discoveredLists.length} ClickUp ${result.lifecycle.discoveredLists.length === 1 ? 'List' : 'Lists'}`
      );
    } catch (error: unknown) {
      toast('error', messageFrom(error, 'Could not discover ClickUp Lists'));
    } finally {
      setBusy(false);
    }
  };

  const setStatusMapping = (
    listId: string,
    status: string,
    state: ClickupLifecycleState
  ) => {
    setMappings((current) =>
      current.map((mapping) => {
        if (mapping.listId !== listId) return mapping;
        const statuses = { ...mapping.statuses, [status]: state };
        const destinations = { ...mapping.destinations };
        for (const [mappedState, destination] of Object.entries(destinations)) {
          if (destination && statuses[destination] !== mappedState)
            delete destinations[mappedState as ClickupLifecycleState];
        }
        destinations[state] ??= status;
        return { ...mapping, statuses, destinations };
      })
    );
  };

  const setListEnabled = (listId: string, enabled: boolean) => {
    setMappings((current) =>
      current.map((mapping) =>
        mapping.listId === listId ? { ...mapping, enabled } : mapping
      )
    );
  };

  const saveLifecycle = async (writeBackEnabled: boolean) => {
    if (!session?.accessToken || !connection) return;
    setBusy(true);
    try {
      const result = await saveClickupLifecycle(
        session.accessToken,
        {
          mappings,
          writeBackEnabled,
          writeBackRules: {
            active: true,
            validation: true,
            delivered: true,
            blocked: false
          }
        },
        activeOrganization?.id
      );
      setConnection(result);
      setMappings(result.lifecycle.mappings);
      setEditingLifecycle(false);
      toast(
        'success',
        writeBackEnabled
          ? 'ClickUp lifecycle sync is enabled'
          : 'ClickUp status mapping saved'
      );
    } catch (error: unknown) {
      toast(
        'error',
        messageFrom(error, 'Could not save ClickUp lifecycle mapping')
      );
    } finally {
      setBusy(false);
    }
  };

  const unmappedCount = useMemo(
    () =>
      (connection?.lifecycle.discoveredLists || []).reduce((total, list) => {
        const mapping = mappings.find((item) => item.listId === list.id);
        return (
          total +
          (mapping?.enabled === false
            ? 0
            : list.statuses.filter(
                (status) => !mapping?.statuses[status.status]
              ).length)
        );
      }, 0),
    [connection?.lifecycle.discoveredLists, mappings]
  );
  const trackedListCount = useMemo(
    () => mappings.filter((mapping) => mapping.enabled !== false).length,
    [mappings]
  );

  if (!connection)
    return (
      <section className="clickupConnection">
        <div className="connectionHeading">
          <div className="connectionLogo">CU</div>
          <div>
            <strong>ClickUp</strong>
            <span>Checking connection…</span>
          </div>
        </div>
      </section>
    );
  const authorized = connection.status !== 'not_connected';
  const chosenWorkspace = connection.availableWorkspaces.find(
    (workspace) => workspace.id === workspaceId
  );

  return (
    <section className="clickupConnection">
      <div className="connectionHeading">
        <div className="connectionLogo">CU</div>
        <div>
          <strong>ClickUp</strong>
          <span>Tasks, owners and delivery status</span>
        </div>
        <span
          className={`connectionState connectionState--${connection.status === 'active' ? 'healthy' : 'pending'}`}
        >
          {connection.status === 'active' ? (
            <CheckCircle2 size={13} />
          ) : (
            <AlertCircle size={13} />
          )}
          {connection.status === 'active'
            ? connection.lifecycle.writeBackEnabled
              ? 'Connected · synced'
              : 'Connected · observing'
            : authorized
              ? 'Finish setup'
              : 'Not connected'}
        </span>
      </div>

      {!connection.platformConfigured ? (
        <div className="connectionNotice">
          <AlertCircle size={16} />
          <div>
            <strong>Platform setup required</strong>
            <span>
              Cindro’s ClickUp OAuth application credentials have not been added
              to this deployment yet.
            </span>
          </div>
        </div>
      ) : null}

      {!authorized ? (
        <div className="connectionIntro">
          <div>
            <h3>Bring task intent into delivery journeys</h3>
            <p>
              Authorize ClickUp, then choose exactly which engineering work
              Cindro can observe. No credentials are copied or pasted.
            </p>
          </div>
          <button
            type="button"
            className="primaryButton"
            disabled={busy || !connection.platformConfigured}
            onClick={authorize}
          >
            <Link2 size={15} />
            Connect ClickUp
          </button>
        </div>
      ) : connection.status === 'configuration_required' ? (
        <div className="connectionSetup">
          <div className="connectionSetupIntro">
            <ShieldCheck size={18} />
            <div>
              <strong>Authorization complete</strong>
              <span>
                Select the Workspace and Spaces that belong in Cindro.
                Everything starts in read-only mode.
              </span>
            </div>
          </div>
          <label>
            <span>ClickUp Workspace</span>
            <select
              value={workspaceId}
              onChange={(event) => {
                setWorkspaceId(event.target.value);
                setSpaceIds([]);
              }}
            >
              <option value="">Choose a Workspace</option>
              {connection.availableWorkspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          </label>
          {connection.availableSpaces.length ? (
            <fieldset className="connectionScopes">
              <legend>Spaces Cindro may observe</legend>
              {connection.availableSpaces.map((space) => (
                <label key={space.id}>
                  <input
                    type="checkbox"
                    checked={spaceIds.includes(space.id)}
                    onChange={(event) =>
                      setSpaceIds((current) =>
                        event.target.checked
                          ? [...current, space.id]
                          : current.filter((id) => id !== space.id)
                      )
                    }
                  />
                  <span>{space.name}</span>
                </label>
              ))}
            </fieldset>
          ) : workspaceId ? (
            <div className="connectionNotice">
              <RefreshCw size={15} />
              <div>
                <strong>Load ClickUp Spaces</strong>
                <span>
                  Choose which Spaces in {chosenWorkspace?.name} contain
                  engineering work.
                </span>
              </div>
            </div>
          ) : null}
          <div className="connectionActions">
            {connection.availableSpaces.length ? (
              <button
                type="button"
                className="primaryButton"
                disabled={busy || !spaceIds.length}
                onClick={activate}
              >
                Connect {spaceIds.length || ''}{' '}
                {spaceIds.length === 1 ? 'Space' : 'Spaces'}
              </button>
            ) : (
              <button
                type="button"
                className="primaryButton"
                disabled={busy || !workspaceId}
                onClick={discover}
              >
                Load Spaces
              </button>
            )}
            <button
              type="button"
              className="secondaryButton"
              disabled={busy}
              onClick={authorize}
            >
              Reauthorize
            </button>
          </div>
        </div>
      ) : (
        <div className="connectionHealth">
          <dl>
            <div>
              <dt>Workspace</dt>
              <dd>{connection.displayName}</dd>
            </div>
            <div>
              <dt>Scope</dt>
              <dd>
                {connection.spaceIds.length}{' '}
                {connection.spaceIds.length === 1 ? 'Space' : 'Spaces'}
              </dd>
            </div>
            <div>
              <dt>Last event</dt>
              <dd>
                {connection.lastWebhookAt
                  ? formatRelativeDate(connection.lastWebhookAt)
                  : 'Waiting for first event'}
              </dd>
            </div>
            <div>
              <dt>Task updates</dt>
              <dd>
                {connection.lifecycle.writeBackEnabled ? 'Automatic' : 'Off'}
              </dd>
            </div>
          </dl>
          {connection.errorMessage ? (
            <div className="connectionNotice">
              <AlertCircle size={15} />
              <div>
                <strong>Connection needs attention</strong>
                <span>{connection.errorMessage}</span>
              </div>
            </div>
          ) : null}
          <div className="lifecycleSetupSummary">
            <div className="lifecycleSetupIcon">
              <Settings2 size={17} />
            </div>
            <div>
              <strong>Lifecycle mapping</strong>
              <span>
                {connection.lifecycle.discoveredLists.length
                  ? `${trackedListCount} of ${connection.lifecycle.discoveredLists.length} ${connection.lifecycle.discoveredLists.length === 1 ? 'List' : 'Lists'} tracked${unmappedCount ? ` · ${unmappedCount} need attention` : ''}`
                  : 'Match existing ClickUp statuses to Cindro automatically.'}
              </span>
            </div>
            <button
              type="button"
              className="secondaryButton"
              disabled={busy}
              onClick={
                connection.lifecycle.discoveredLists.length
                  ? () => setEditingLifecycle((current) => !current)
                  : discoverLifecycle
              }
            >
              {connection.lifecycle.discoveredLists.length ? (
                <>
                  <Settings2 size={14} />
                  {editingLifecycle ? 'Close' : 'Review mapping'}
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Map statuses
                </>
              )}
            </button>
          </div>
          {editingLifecycle ? (
            <div className="lifecycleMappingEditor">
              <div className="lifecycleMappingIntro">
                <div>
                  <strong>Use each List as it is</strong>
                  <span>
                    Cindro has recommended the closest lifecycle meaning. Only
                    change anything that looks wrong.
                  </span>
                </div>
                <span
                  className={`mappingCoverage ${unmappedCount ? 'mappingCoverage--attention' : ''}`}
                >
                  {unmappedCount
                    ? `${unmappedCount} unmapped`
                    : 'Ready to sync'}
                </span>
              </div>
              <div className="lifecycleListTable">
                {connection.lifecycle.discoveredLists.map((list) => {
                  const mapping = mappings.find(
                    (item) => item.listId === list.id
                  );
                  return (
                    <section
                      key={list.id}
                      className={`lifecycleListGroup ${mapping?.enabled === false ? 'lifecycleListGroup--ignored' : ''}`}
                    >
                      <header>
                        <div>
                          <strong>{list.name}</strong>
                          <span>
                            {list.folderName
                              ? `${list.spaceName} / ${list.folderName}`
                              : list.spaceName}
                          </span>
                        </div>
                        <label className="lifecycleListToggle">
                          <input
                            type="checkbox"
                            checked={mapping?.enabled !== false}
                            onChange={(event) =>
                              setListEnabled(list.id, event.target.checked)
                            }
                          />
                          <span>
                            {mapping?.enabled === false ? 'Ignored' : 'Tracked'}
                          </span>
                        </label>
                      </header>
                      <div className="lifecycleStatusRows">
                        {list.statuses.map((status) => (
                          <label
                            key={status.status}
                            className="lifecycleStatusRow"
                          >
                            <span className="nativeStatus">
                              <i
                                style={{
                                  background:
                                    status.color || 'var(--text-faint)'
                                }}
                              />
                              {status.status}
                            </span>
                            <ArrowRight size={13} />
                            <select
                              disabled={mapping?.enabled === false}
                              aria-label={`Lifecycle for ${list.name} ${status.status}`}
                              value={mapping?.statuses[status.status] || ''}
                              onChange={(event) =>
                                setStatusMapping(
                                  list.id,
                                  status.status,
                                  event.target.value as ClickupLifecycleState
                                )
                              }
                            >
                              <option value="">Choose meaning</option>
                              {LIFECYCLE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
              <div className="lifecycleMappingFooter">
                <div>
                  <ShieldCheck size={15} />
                  <span>
                    Sync can move tasks forward to Active, Validation or
                    Delivered. It never changes their List or moves them
                    backwards.
                  </span>
                </div>
                <div>
                  <button
                    type="button"
                    className="secondaryButton"
                    disabled={busy}
                    onClick={() => void saveLifecycle(false)}
                  >
                    Save mapping only
                  </button>
                  <button
                    type="button"
                    className="primaryButton"
                    disabled={busy || Boolean(unmappedCount)}
                    onClick={() => void saveLifecycle(true)}
                  >
                    {connection.lifecycle.writeBackEnabled
                      ? 'Save lifecycle sync'
                      : 'Enable lifecycle sync'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          <div className="connectionActions">
            <button
              type="button"
              className="secondaryButton"
              disabled={busy}
              onClick={authorize}
            >
              <RefreshCw size={14} />
              Reauthorize
            </button>
            <button
              type="button"
              className="textButton"
              disabled={busy}
              onClick={disconnect}
            >
              Disconnect
            </button>
            <a
              href={`/org/${activeOrganization?.slug || ''}/guide/clickup-rollout`}
              className="textButton"
            >
              Setup guide
              <ExternalLink size={13} />
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
